/**
 * PDF layout editor.
 *
 * The matter-record PDF (functions/src/documents/matterRecordPdf.js) renders the same
 * pages and fields the live form does, one per line, full width, no formatting, by
 * default — this page is a real visual page-layout designer for the PRINTED record,
 * independent of the on-screen form. The editing surface is styled to look like the
 * actual page (fixed width, PDF-matching margins/typeface, real sample values) rather
 * than a settings form, so what you see here is close to what prints:
 *
 *   - Drag a field directly onto another field to pair them on one printed line (drop on
 *     its left/right half to land before/after it).
 *   - Drag the divider between two paired fields to resize them (continuous, not fixed
 *     steps) — the pool of width they share stays fixed, so one grows as the other shrinks.
 *   - A paired field always shows a small "⤢ Own line" control — no need to find the
 *     right drop target to undo a pairing.
 *   - Bold / italic / underline / font size per field, via the "Aa" toolbar.
 *   - "Hide" removes a field from the PDF entirely; "Show hidden fields" reveals what's
 *     hidden in that section, each with a "Show" button to bring it back.
 *   - Every printed label, page heading, and the title block (firm name line, document
 *     title, italic tagline) is click-to-edit in place (PDF-only wording, stored as
 *     pdfLayout.labels / pdfLayout.sectionTitles / pdfLayout.header; the form is
 *     untouched, and blanking the text restores the original wording).
 *   - "Preview PDF" renders the real pdfkit output on demand (exact pagination/line-wrap,
 *     real field values from a real matter if you pick one) without saving anything first.
 *
 * A field/page never touched keeps its natural form-order position and full width — an
 * entirely unset settings/pdfLayout document is a true no-op, EXCEPT for one deliberate
 * default: notice banners (instructional copy for the form, not an answer) are hidden
 * from the PDF out of the box. See pdfLayoutRows.js's header on both the web and
 * functions side for the full fallback/hiding contract.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useDraggable, useDroppable, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { CallStatus, useCallable } from '../../lib/useCallable';
import { useDoc } from '../../lib/useFirestore';
import { buildEffectiveSections } from '../../lib/fieldLayout';
import { FORM_SECTIONS } from '../../lib/formSchema';
import { sampleValueFor } from '../../lib/pdfLayoutSample';
import {
  cleanPdfHeader,
  hideField,
  mergeFieldIntoRow,
  moveFieldToOwnRow,
  patchFieldFormat,
  renameField,
  renameSection,
  resizeAdjacentFields,
  resolvePdfLayoutRows,
  showField,
  toPdfLayoutDoc,
} from '../../lib/pdfLayoutRows';

const sectionDragId = (id) => `section:${id}`;
const fieldDragId = (path) => `field:${path}`;
const PAGE_FONT = "'Helvetica Neue', Helvetica, Arial, sans-serif";

/** Turns a base64 PDF payload into a blob URL and opens it in a new tab. */
function openPdfBase64(base64) {
  const bytes = atob(base64);
  const buffer = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) buffer[i] = bytes.charCodeAt(i);
  const url = URL.createObjectURL(new Blob([buffer], { type: 'application/pdf' }));
  window.open(url, '_blank', 'noopener');
  // Revoke well after the new tab has had time to load it.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export default function AdminPdfLayout() {
  const { data: fieldLayout, loading: loadingFieldLayout } = useDoc('settings/fieldLayout');
  const { data: storedPdfLayout, loading: loadingPdfLayout } = useDoc('settings/pdfLayout');
  // For the title block's live defaults (the printed firm name comes from settings/general).
  const { data: general } = useDoc('settings/general');
  const { run, busy, error, saved } = useCallable();
  const previewCall = useCallable();
  const [sections, setSections] = useState(null);
  const [header, setHeader] = useState(null);
  const [followFormRows, setFollowFormRows] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const naturalSections = useMemo(
    () => (loadingFieldLayout ? null : buildEffectiveSections(FORM_SECTIONS, fieldLayout || {})),
    [fieldLayout, loadingFieldLayout]
  );

  useEffect(() => {
    if (sections || !naturalSections || loadingPdfLayout) return;
    setSections(resolvePdfLayoutRows(naturalSections, storedPdfLayout));
    setFollowFormRows(storedPdfLayout?.followFormRows === true);
    const storedHeader = storedPdfLayout?.header;
    setHeader({
      firmName: storedHeader?.firmName || '',
      title: storedHeader?.title || '',
      tagline: storedHeader?.tagline || '',
      numbers: storedHeader?.numbers || '',
      showTagline: storedHeader?.showTagline !== false,
      // 'auto' is the editor-only third state; cleanPdfHeader drops any non-boolean, so
      // only an explicit Always/Never choice ever reaches the saved document.
      showNumbers: typeof storedHeader?.showNumbers === 'boolean' ? storedHeader.showNumbers : 'auto',
    });
  }, [sections, naturalSections, storedPdfLayout, loadingPdfLayout]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  if (!sections) {
    return <p className="text-sm text-slate-500 dark:text-slate-400">Loading the effective form…</p>;
  }

  const draftDoc = (flag = followFormRows) => {
    const doc = toPdfLayoutDoc(sections, { followFormRows: flag });
    const cleaned = cleanPdfHeader(header);
    return cleaned ? { ...doc, header: cleaned } : doc;
  };
  // Toggling re-resolves the draft so the WYSIWYG page shows exactly what the flag does:
  // ON re-groups every section per the form's grid; OFF keeps the current (form-mirrored)
  // arrangement as an ordinary editable starting point.
  const toggleFollowFormRows = () => {
    const next = !followFormRows;
    setFollowFormRows(next);
    setSections(resolvePdfLayoutRows(naturalSections, draftDoc(next)));
  };
  const save = () => run('adminUpdateSettings', { docId: 'pdfLayout', value: draftDoc() });
  const restoreDefault = () => {
    setSections(resolvePdfLayoutRows(naturalSections, undefined));
    setFollowFormRows(false);
    setHeader({ firmName: '', title: '', tagline: '', numbers: '', showNumbers: 'auto', showTagline: true });
  };
  const preview = async () => {
    const result = await previewCall.run('previewPdfLayout', { pdfLayout: draftDoc() });
    if (result?.pdfBase64) openPdfBase64(result.pdfBase64);
  };
  const unlinkField = (sectionId, path, rowIndex) =>
    setSections((prev) => moveFieldToOwnRow(prev, sectionId, path, rowIndex + 1));
  const onHide = (sectionId, path) => setSections((prev) => hideField(prev, sectionId, path));
  const onShow = (sectionId, path) => setSections((prev) => showField(prev, sectionId, path));

  const handleDragEnd = ({ active, over }) => {
    setDragActive(false);
    if (!over) return;
    const activeData = active.data.current;

    if (activeData?.type === 'section') {
      if (active.id === over.id) return;
      setSections((prev) => {
        const oldIndex = prev.findIndex((s) => sectionDragId(s.id) === active.id);
        const newIndex = prev.findIndex((s) => sectionDragId(s.id) === over.id);
        if (oldIndex === -1 || newIndex === -1) return prev;
        return arrayMove(prev, oldIndex, newIndex);
      });
      return;
    }

    if (activeData?.type !== 'field') return;
    const overData = over.data.current;
    if (!overData || overData.sectionId !== activeData.sectionId) return; // no cross-section pairing

    if (overData.type === 'field-drop') {
      if (overData.path === activeData.path) return;
      const activeRect = active.rect.current.translated;
      const overRect = over.rect;
      const activeCenterX = activeRect ? activeRect.left + activeRect.width / 2 : overRect.left;
      const side = activeCenterX < overRect.left + overRect.width / 2 ? 'left' : 'right';
      setSections((prev) => mergeFieldIntoRow(prev, activeData.sectionId, activeData.path, overData.path, side));
    } else if (overData.type === 'row-gap') {
      setSections((prev) => moveFieldToOwnRow(prev, activeData.sectionId, activeData.path, overData.index));
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg bg-white p-4 shadow dark:bg-slate-900">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">PDF layout</h2>
            <p className="mt-1 max-w-2xl text-xs text-slate-500 dark:text-slate-400">
              The page below is a close approximation of the printed record — sample values,
              not a real matter's. Drag a field onto another to put them on one line and
              drag the divider to resize; use "⤢ Own line" to split a paired field back out.
              Click any label, page heading, or the title block at the top to reword how it
              prints (blank it to restore the original wording). This only affects the printed record, not the live
              intake form.
            </p>
            <label className="mt-2 flex items-start gap-2 text-xs text-slate-600 dark:text-slate-300">
              <input type="checkbox" className="mt-0.5" checked={followFormRows} onChange={toggleFollowFormRows} />
              <span>
                <strong>Match the form's side-by-side layout.</strong> Print rows mirror the live
                form's grid and re-flow per matter as conditional fields appear or disappear —
                hand-arranging rows is off while this is on (hiding, formatting and wording still
                apply).
              </span>
            </label>
          </div>
          <div className="flex items-center gap-2">
            <CallStatus busy={busy} error={error} saved={saved} />
            <button
              type="button"
              onClick={restoreDefault}
              className="rounded border border-slate-300 px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              Restore form order
            </button>
            <button
              type="button"
              onClick={preview}
              disabled={previewCall.busy}
              className="rounded border border-firm-300 px-3 py-2 text-sm text-firm-700 hover:bg-firm-50 disabled:opacity-50 dark:border-firm-700 dark:text-firm-100 dark:hover:bg-firm-950"
            >
              {previewCall.busy ? 'Rendering…' : 'Preview PDF'}
            </button>
            <button
              type="button"
              onClick={save}
              disabled={busy}
              className="rounded bg-firm-700 px-4 py-2 text-sm font-medium text-white hover:bg-firm-900 disabled:opacity-50 dark:bg-firm-500 dark:hover:bg-firm-400"
            >
              Save PDF layout
            </button>
          </div>
        </div>
        {previewCall.error && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{previewCall.error}</p>}
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={() => setDragActive(true)}
        onDragCancel={() => setDragActive(false)}
        onDragEnd={handleDragEnd}
      >
        {/* The "page": fixed width, PDF-matching margin/typeface/colors, white background
            with a print-like shadow — this IS the preview, not a settings panel next to one. */}
        <div
          className="mx-auto max-w-3xl rounded bg-white p-12 shadow-lg dark:bg-slate-950 dark:shadow-black/40"
          style={{ fontFamily: PAGE_FONT, color: '#111111' }}
        >
          {/* The title block, editable in place like everything else on this page. Each
              line's placeholder is what prints when left blank, so an untouched header
              shows (and keeps) the historical wording. */}
          <div className="mb-4 border-b-2 pb-2 text-center" style={{ borderColor: '#1F3864' }}>
            <input
              className="w-full rounded bg-transparent text-center text-[17px] font-bold uppercase outline-none placeholder:text-slate-300 hover:bg-slate-50 focus:bg-slate-50 dark:placeholder:text-slate-600 dark:hover:bg-slate-900 dark:focus:bg-slate-900"
              style={{ color: '#111111' }}
              value={header?.firmName || ''}
              onChange={(e) => setHeader((prev) => ({ ...prev, firmName: e.target.value }))}
              placeholder={(general?.firmName || 'Law Firm').toUpperCase()}
              aria-label="Printed firm name line"
              title="Edit the firm name line as it prints (blank = the firm name from Settings)"
            />
            <input
              className="w-full rounded bg-transparent text-center text-[14px] font-bold uppercase outline-none placeholder:text-slate-300 hover:bg-slate-50 focus:bg-slate-50 dark:placeholder:text-slate-600 dark:hover:bg-slate-900 dark:focus:bg-slate-900"
              style={{ color: '#111111' }}
              value={header?.title || ''}
              onChange={(e) => setHeader((prev) => ({ ...prev, title: e.target.value }))}
              placeholder="NEW MATTER MEMO"
              aria-label="Printed document title"
              title="Edit the document title as it prints"
            />
            <div className="flex items-center justify-center gap-1 text-[10px] italic" style={{ color: '#444444' }}>
              <input
                className={`rounded bg-transparent text-right italic outline-none placeholder:text-slate-300 hover:bg-slate-50 focus:bg-slate-50 dark:placeholder:text-slate-600 dark:hover:bg-slate-900 dark:focus:bg-slate-900 ${
                  header?.showTagline === false ? 'line-through opacity-40' : ''
                }`}
                style={{ color: '#444444', width: '18rem' }}
                value={header?.tagline || ''}
                onChange={(e) => setHeader((prev) => ({ ...prev, tagline: e.target.value }))}
                placeholder="System-generated approved record"
                aria-label="Printed tagline"
                title="Edit the italic tagline as it prints"
              />
              <select
                className="rounded border border-slate-200 bg-transparent px-1 py-0.5 text-[9px] not-italic text-slate-400 dark:border-slate-700 dark:text-slate-500"
                value={header?.showTagline === false ? 'hidden' : 'shown'}
                onChange={(e) => setHeader((prev) => ({ ...prev, showTagline: e.target.value === 'shown' }))}
                aria-label="Whether the tagline prints"
                title="Print or drop the tagline phrase; the numbers line is controlled separately"
              >
                <option value="shown">Shown</option>
                <option value="hidden">Hidden</option>
              </select>
              <span>—</span>
              <input
                className="rounded bg-transparent italic outline-none placeholder:text-slate-300 hover:bg-slate-50 focus:bg-slate-50 dark:placeholder:text-slate-600 dark:hover:bg-slate-900 dark:focus:bg-slate-900"
                style={{ color: '#444444', width: '22rem' }}
                value={header?.numbers || ''}
                onChange={(e) => setHeader((prev) => ({ ...prev, numbers: e.target.value }))}
                placeholder="Client No. {clientNumber} / Matter No. {matterNumber}"
                aria-label="Printed client/matter numbers line"
                title="Edit the numbers line; {clientNumber} and {matterNumber} print the real numbers (or 'pending')"
              />
              <select
                className="rounded border border-slate-200 bg-transparent px-1 py-0.5 text-[9px] not-italic text-slate-400 dark:border-slate-700 dark:text-slate-500"
                value={header?.showNumbers === true ? 'always' : header?.showNumbers === false ? 'never' : 'auto'}
                onChange={(e) =>
                  setHeader((prev) => ({
                    ...prev,
                    showNumbers: e.target.value === 'always' ? true : e.target.value === 'never' ? false : 'auto',
                  }))
                }
                aria-label="When the numbers line prints"
                title="Auto hides this line while the workflow is off and no numbers are assigned; an assigned number always prints in Auto"
              >
                <option value="auto">Auto</option>
                <option value="always">Always</option>
                <option value="never">Never</option>
              </select>
            </div>
          </div>
          <SortableContext items={sections.map((s) => sectionDragId(s.id))} strategy={verticalListSortingStrategy}>
            <div className="space-y-6">
              {sections.map((section, index) => (
                <SectionBlock
                  key={section.id}
                  section={section}
                  formFlow={followFormRows}
                  dragActive={dragActive}
                  onMoveSection={(delta) =>
                    setSections((prev) => {
                      const to = index + delta;
                      if (to < 0 || to >= prev.length) return prev;
                      return arrayMove(prev, index, to);
                    })
                  }
                  onResize={(rowIndex, leftPath, rightPath, deltaPercent) =>
                    setSections((prev) => resizeAdjacentFields(prev, section.id, rowIndex, leftPath, rightPath, deltaPercent))
                  }
                  onFormat={(path, patch) => setSections((prev) => patchFieldFormat(prev, path, patch))}
                  onUnlink={unlinkField}
                  onHide={onHide}
                  onShow={onShow}
                  onRenameField={(path, label) => setSections((prev) => renameField(prev, path, label))}
                  onRenameSection={(title) => setSections((prev) => renameSection(prev, section.id, title))}
                />
              ))}
            </div>
          </SortableContext>
        </div>
      </DndContext>
    </div>
  );
}

function SectionBlock({ section, formFlow, dragActive, onMoveSection, onResize, onFormat, onUnlink, onHide, onShow, onRenameField, onRenameSection }) {
  const sortable = useSortable({ id: sectionDragId(section.id), data: { type: 'section' } });
  const style = { transform: CSS.Transform.toString(sortable.transform), transition: sortable.transition };
  const [showHidden, setShowHidden] = useState(false);

  return (
    <section ref={sortable.setNodeRef} style={style} className="group/section">
      <div className="flex items-center gap-2 border-b border-slate-300 pb-1 dark:border-slate-700">
        {/* The heading IS the editor: click it and type. PDF-only — the form's page title
            is unchanged; blanking it falls back to the form title on save. */}
        <input
          className="flex-1 rounded bg-transparent text-[15px] font-bold outline-none hover:bg-slate-50 focus:bg-slate-50 dark:hover:bg-slate-900 dark:focus:bg-slate-900"
          style={{ color: '#1F3864' }}
          value={section.title}
          onChange={(e) => onRenameSection(e.target.value)}
          aria-label={`Printed heading for ${section.baseTitle}`}
          title="Edit how this page heading prints on the PDF (the form is unchanged)"
        />
        <button
          type="button"
          ref={sortable.setActivatorNodeRef}
          {...sortable.attributes}
          {...sortable.listeners}
          className="cursor-grab rounded px-1 text-xs text-slate-300 opacity-0 hover:bg-slate-100 hover:text-slate-600 group-hover/section:opacity-100 active:cursor-grabbing dark:hover:bg-slate-800"
          title="Drag to reorder page"
          aria-label={`Drag to reorder ${section.title}`}
        >
          ⠿
        </button>
        <button
          type="button"
          onClick={() => onMoveSection(-1)}
          className="rounded px-1 text-xs text-slate-300 opacity-0 hover:bg-slate-100 hover:text-slate-600 group-hover/section:opacity-100 dark:hover:bg-slate-800"
          aria-label={`Move ${section.title} up`}
        >
          ↑
        </button>
        <button
          type="button"
          onClick={() => onMoveSection(1)}
          className="rounded px-1 text-xs text-slate-300 opacity-0 hover:bg-slate-100 hover:text-slate-600 group-hover/section:opacity-100 dark:hover:bg-slate-800"
          aria-label={`Move ${section.title} down`}
        >
          ↓
        </button>
      </div>

      <div className="mt-1">
        {!formFlow && <RowGap sectionId={section.id} index={0} active={dragActive} />}
        {section.rows.map((row, rowIndex) => (
          <div key={row.fields.map((f) => f.path).join('|')}>
            <FieldRow
              row={row}
              rowIndex={rowIndex}
              sectionId={section.id}
              formFlow={formFlow}
              onResize={onResize}
              onFormat={onFormat}
              onUnlink={onUnlink}
              onHide={onHide}
              onRenameField={onRenameField}
            />
            {!formFlow && <RowGap sectionId={section.id} index={rowIndex + 1} active={dragActive} />}
          </div>
        ))}
      </div>

      {section.hiddenFields.length > 0 && (
        <div className="mt-2 border-t border-dashed border-slate-200 pt-1 dark:border-slate-700">
          <button
            type="button"
            onClick={() => setShowHidden((v) => !v)}
            className="text-[11px] font-normal normal-case tracking-normal text-slate-400 hover:text-firm-700 dark:hover:text-firm-200"
            style={{ fontFamily: 'inherit' }}
          >
            {showHidden ? '▾' : '▸'} {showHidden ? 'Hide' : 'Show'} hidden fields ({section.hiddenFields.length})
          </button>
          {showHidden && (
            <ul className="mt-1 flex flex-wrap gap-1.5">
              {section.hiddenFields.map((field) => (
                <li
                  key={field.path}
                  className="flex items-center gap-1.5 rounded border border-slate-200 bg-slate-50 px-2 py-1 text-xs text-slate-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400"
                >
                  {field.label}
                  <button
                    type="button"
                    onClick={() => onShow(section.id, field.path)}
                    className="font-medium text-firm-700 hover:underline dark:text-firm-200"
                  >
                    Show
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}

/**
 * A drop target between rows for pulling a field out to its own line — only rendered
 * (and only visible) during an active drag, so the page reads clean at rest instead of
 * showing an empty gap strip between every single line all the time.
 */
function RowGap({ sectionId, index, active }) {
  const droppable = useDroppable({ id: `row-gap:${sectionId}:${index}`, data: { type: 'row-gap', sectionId, index } });
  if (!active) return <div ref={droppable.setNodeRef} className="h-0" />;
  return (
    <div
      ref={droppable.setNodeRef}
      className={`my-1 h-3 rounded border border-dashed transition-colors ${
        droppable.isOver
          ? 'border-firm-500 bg-firm-100 dark:bg-firm-900'
          : 'border-slate-300 dark:border-slate-700'
      }`}
    />
  );
}

function FieldRow({ row, rowIndex, sectionId, formFlow, onResize, onFormat, onUnlink, onHide, onRenameField }) {
  const rowRef = useRef(null);
  const isPaired = row.fields.length > 1;

  return (
    <div ref={rowRef} className="flex items-stretch gap-0 py-1.5">
      {row.fields.map((field, index) => (
        <div key={field.path} className="flex items-stretch" style={{ width: `${field.widthPercent}%` }}>
          <FieldBox
            field={field}
            sectionId={sectionId}
            formFlow={formFlow}
            onFormat={onFormat}
            isPaired={isPaired}
            onUnlink={() => onUnlink(sectionId, field.path, rowIndex)}
            onHide={() => onHide(sectionId, field.path)}
            onRename={(label) => onRenameField(field.path, label)}
          />
          {!formFlow && index < row.fields.length - 1 && (
            <ResizeDivider
              rowRef={rowRef}
              onResize={(deltaPercent) => onResize(rowIndex, field.path, row.fields[index + 1].path, deltaPercent)}
            />
          )}
        </div>
      ))}
    </div>
  );
}

function ResizeDivider({ rowRef, onResize }) {
  const dragState = useRef(null);

  const onPointerDown = (event) => {
    event.preventDefault();
    const rowWidth = rowRef.current?.getBoundingClientRect().width || 1;
    dragState.current = { startX: event.clientX, rowWidth };
    const onPointerMove = (moveEvent) => {
      if (!dragState.current) return;
      const deltaPx = moveEvent.clientX - dragState.current.startX;
      const deltaPercent = (deltaPx / dragState.current.rowWidth) * 100;
      onResize(deltaPercent);
      dragState.current.startX = moveEvent.clientX;
    };
    const onPointerUp = () => {
      dragState.current = null;
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
  };

  return (
    <div
      onPointerDown={onPointerDown}
      className="mx-1 w-1 shrink-0 cursor-col-resize rounded bg-slate-200 hover:bg-firm-400 dark:bg-slate-700 dark:hover:bg-firm-400"
      title="Drag to resize"
      role="separator"
      aria-orientation="vertical"
    />
  );
}

function FieldBox({ field, sectionId, formFlow, onFormat, isPaired, onUnlink, onHide, onRename }) {
  const [formatOpen, setFormatOpen] = useState(false);
  const draggable = useDraggable({ id: fieldDragId(field.path), data: { type: 'field', path: field.path, sectionId } });
  const droppable = useDroppable({
    id: `${fieldDragId(field.path)}:drop`,
    data: { type: 'field-drop', path: field.path, sectionId },
  });
  const style = draggable.transform
    ? { transform: CSS.Translate.toString(draggable.transform), zIndex: 10, position: 'relative' }
    : undefined;
  const format = field.format || {};
  const valueStyle = {
    fontWeight: format.bold ? 'bold' : 'normal',
    fontStyle: format.italic ? 'italic' : 'normal',
    textDecoration: format.underline ? 'underline' : 'none',
    fontSize: `${format.fontSize || 10}px`,
  };

  return (
    <div
      ref={(node) => {
        draggable.setNodeRef(node);
        droppable.setNodeRef(node);
      }}
      style={style}
      className={`group/field relative min-w-0 flex-1 rounded px-1.5 py-1 transition-colors ${
        droppable.isOver ? 'bg-firm-50 outline outline-1 outline-firm-300 dark:bg-firm-950' : 'hover:bg-slate-50 dark:hover:bg-slate-900'
      }`}
    >
      <div className="flex items-start justify-between gap-1">
        {/* The printed label IS the editor — click and type. PDF-only; the form keeps
            its own label, and blanking this falls back to the form label on save. */}
        <input
          className="w-full min-w-0 rounded bg-transparent text-[8px] font-bold uppercase tracking-wide outline-none hover:bg-slate-100 focus:bg-slate-100 dark:hover:bg-slate-800 dark:focus:bg-slate-800"
          style={{ color: '#444444' }}
          value={field.label}
          onChange={(e) => onRename(e.target.value)}
          aria-label={`Printed label for ${field.baseLabel || field.path}`}
          title="Edit how this label prints on the PDF (the form is unchanged)"
        />
        <div className="flex shrink-0 items-center gap-0.5 opacity-0 group-hover/field:opacity-100">
          {!formFlow && (
            <button
              type="button"
              {...draggable.attributes}
              {...draggable.listeners}
              className="cursor-grab rounded px-1 text-[10px] text-slate-400 hover:bg-slate-100 active:cursor-grabbing dark:hover:bg-slate-800"
              title="Drag to pair with another field"
              aria-label={`Drag ${field.label}`}
            >
              ⠿
            </button>
          )}
          {!formFlow && isPaired && (
            <button
              type="button"
              onClick={onUnlink}
              className="rounded px-1 text-[10px] text-slate-400 hover:bg-slate-100 hover:text-firm-700 dark:hover:bg-slate-800 dark:hover:text-firm-200"
              title="Move this field back to its own line"
            >
              ⤢ Own line
            </button>
          )}
          <button
            type="button"
            onClick={() => setFormatOpen((v) => !v)}
            className="rounded px-1 text-[10px] italic text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
            title="Text formatting"
            aria-label={`Formatting for ${field.label}`}
          >
            Aa
          </button>
          <button
            type="button"
            onClick={onHide}
            className="rounded px-1 text-[10px] text-slate-400 hover:bg-slate-100 hover:text-red-700 dark:hover:bg-slate-800 dark:hover:text-red-300"
            title="Remove this field from the PDF entirely"
          >
            Hide
          </button>
        </div>
      </div>
      <p className="truncate" style={valueStyle}>
        {sampleValueFor(field)}
      </p>

      {formatOpen && (
        <div className="absolute right-0 top-full z-20 mt-1 w-48 rounded border border-slate-200 bg-white p-2 text-xs shadow-lg dark:border-slate-700 dark:bg-slate-900">
          <div className="flex flex-wrap gap-2">
            <label className="flex items-center gap-1">
              <input type="checkbox" checked={Boolean(format.bold)} onChange={(e) => onFormat(field.path, { bold: e.target.checked })} />
              Bold
            </label>
            <label className="flex items-center gap-1">
              <input type="checkbox" checked={Boolean(format.italic)} onChange={(e) => onFormat(field.path, { italic: e.target.checked })} />
              Italic
            </label>
            <label className="flex items-center gap-1">
              <input
                type="checkbox"
                checked={Boolean(format.underline)}
                onChange={(e) => onFormat(field.path, { underline: e.target.checked })}
              />
              Underline
            </label>
            <label className="flex w-full items-center gap-1">
              Size
              <input
                type="number"
                min={6}
                max={24}
                className="w-14 rounded border border-slate-300 px-1 py-0.5 dark:border-slate-600 dark:bg-slate-800"
                value={format.fontSize || 10}
                onChange={(e) => onFormat(field.path, { fontSize: Number(e.target.value) || 10 })}
              />
            </label>
          </div>
        </div>
      )}
    </div>
  );
}
